import React, { useState, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  alpha,
} from "@mui/material";
import { Edit, Replay, EventRepeat } from "@mui/icons-material";
import ViewColumnRounded from "@mui/icons-material/ViewColumnRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import { useTheme } from "@mui/material";
import { Transaction, TProvider } from "../utils/types";
import { rotate360 } from "../utils/utils";
import StatusPill from "./StatusPill";
import { CustomDialogHandle } from "./CustomDialog";
import { translate } from "../utils/currency";

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString("ru-RU"),
    time: date.toLocaleTimeString("ru-RU", { hour12: false }),
  };
};

/**
 * Колонки описаны декларативно, чтобы их можно было скрывать.
 * `defaultHidden` — редко используемые поля: по умолчанию таблица помещается
 * в экран без горизонтальной прокрутки, остальное включается через меню.
 */
type ColumnId =
  | "merchant"
  | "id"
  | "requisites"
  | "method"
  | "bank"
  | "status"
  | "orderId"
  | "provider"
  | "type"
  | "user"
  | "amount"
  | "paid"
  | "date";

const COLUMNS: { id: ColumnId; label: string; defaultHidden?: boolean }[] = [
  { id: "merchant", label: "Мерчант" },
  { id: "id", label: "ID" },
  { id: "requisites", label: "Реквизиты" },
  { id: "method", label: "Метод" },
  { id: "bank", label: "Банк", defaultHidden: true },
  { id: "status", label: "Статус" },
  { id: "orderId", label: "Заказ" },
  { id: "provider", label: "Провайдер" },
  { id: "type", label: "Тип", defaultHidden: true },
  { id: "user", label: "Пользователь", defaultHidden: true },
  { id: "amount", label: "Сумма" },
  { id: "paid", label: "Подтв." },
  { id: "date", label: "Дата" },
];

const VISIBILITY_KEY = "transactions.columns";
const DENSITY_KEY = "transactions.density";

const readVisibility = (): Record<string, boolean> => {
  const defaults = Object.fromEntries(
    COLUMNS.map((c) => [c.id, !c.defaultHidden]),
  );
  try {
    const saved = localStorage.getItem(VISIBILITY_KEY);
    return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
  } catch {
    return defaults;
  }
};

const TransactionsTable: React.FC<{
  dialogRef: React.RefObject<CustomDialogHandle | null>;
  setTransaction: React.Dispatch<React.SetStateAction<Transaction | null>>;
  /** Клик по строке раскрывает подробности операции. */
  onOpenDetails?: (t: Transaction) => void;
  transactions: Transaction[];
  providers: TProvider[];
  setChangeType: React.Dispatch<React.SetStateAction<string>>;
  merchants: TProvider[];
  sendCallBack: (id: string) => void;
}> = ({
  dialogRef,
  setTransaction,
  onOpenDetails,
  transactions,
  providers,
  setChangeType,
  merchants,
  sendCallBack,
}) => {
  const theme = useTheme();
  const [idType, setIdType] = useState<"merchant" | "provider">("provider");
  const [visible, setVisible] =
    useState<Record<string, boolean>>(readVisibility);
  const [dense, setDense] = useState<boolean>(
    () => localStorage.getItem(DENSITY_KEY) !== "comfortable",
  );
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);

  const providerMap = useMemo(() => {
    const map = new Map();
    providers.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [providers]);

  const merchantMap = useMemo(() => {
    const map = new Map();
    merchants.forEach((m) => map.set(m.id, m.name));
    return map;
  }, [merchants]);

  const shownColumns = useMemo(
    () => COLUMNS.filter((c) => visible[c.id]),
    [visible],
  );

  const toggleColumn = useCallback((id: ColumnId) => {
    setVisible((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(VISIBILITY_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const handleDensity = (_: unknown, value: "dense" | "comfortable" | null) => {
    if (!value) return;
    setDense(value === "dense");
    localStorage.setItem(DENSITY_KEY, value);
  };

  /** Копирование и действия не должны выделять строку. */
  const stop = (event: React.MouseEvent) => event.stopPropagation();

  const handleCopyClick = (event: React.MouseEvent, value: string) => {
    event.stopPropagation();
    navigator.clipboard.writeText(value);
  };

  const cellPadding = dense ? "6px 8px" : "12px 12px";

  const copyButton = (value: string, label: string) => (
    <Tooltip title={`Скопировать ${label}`}>
      <IconButton
        aria-label={`Скопировать ${label}`}
        size="small"
        onClick={(event) => handleCopyClick(event, value)}
        sx={{ width: 30, height: 30, flexShrink: 0 }}
      >
        <ContentCopyRounded sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );

  const renderCell = (column: ColumnId, t: Transaction) => {
    switch (column) {
      case "merchant":
        return (
          <Typography variant="body2" fontWeight={620} noWrap>
            {merchantMap.get(t.merch_id) || "—"}
          </Typography>
        );

      case "id":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              maxWidth: 140,
            }}
          >
            <Tooltip title={t.id}>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", fontSize: 12.5 }}
                noWrap
              >
                {t.id}
              </Typography>
            </Tooltip>
            {copyButton(t.id, "ID транзакции")}
          </Box>
        );

      case "requisites":
        return (
          <Box sx={{ maxWidth: 150 }}>
            <Tooltip
              title={
                (t.payment_method === "bol_qr_code" ? t.qr_code : t.number) ||
                "—"
              }
            >
              <Typography
                variant="body2"
                noWrap
                sx={{ fontFamily: "monospace", fontSize: 12.5 }}
              >
                {(t.payment_method === "bol_qr_code" ? t.qr_code : t.number) ||
                  "—"}
              </Typography>
            </Tooltip>
            <Tooltip title={t.fullname || "—"}>
              <Typography variant="caption" color="text.secondary" noWrap
                sx={{ display: "block" }}
              >
                {t.fullname || "—"}
              </Typography>
            </Tooltip>
          </Box>
        );

      case "method":
        return (
          <Typography variant="body2" noWrap>
            {translate(t.payment_method)}
          </Typography>
        );

      case "bank":
        return (
          <Typography variant="body2" noWrap>
            {t.bank_name || "—"}
          </Typography>
        );

      case "status":
        return <StatusPill status={t.status} dense={dense} />;

      case "orderId": {
        const value =
          (idType === "merchant" ? t.merchant_order_id : t.mid_order_id) || "—";
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              maxWidth: 140,
            }}
          >
            <Tooltip title={value}>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontFamily: "monospace", fontSize: 12.5 }}
              >
                {value}
              </Typography>
            </Tooltip>
            {copyButton(value, "номер заказа")}
          </Box>
        );
      }

      case "provider":
        return (
          <Typography variant="body2" noWrap>
            {providerMap.get(t.payment_terminal_id) || "—"}
          </Typography>
        );

      case "type":
        return (
          <Typography variant="body2" noWrap>
            {t.transaction_type}
          </Typography>
        );

      case "user":
        return (
          <Typography
            variant="body2"
            noWrap
            sx={{ fontFamily: "monospace", fontSize: 12.5, maxWidth: 120 }}
          >
            {t.user_id || "—"}
          </Typography>
        );

      case "amount":
        return (
          <Box sx={{ textAlign: "right" }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
            >
              {t.start_amount.toLocaleString("ru-RU")}{" "}
              <Typography component="span" variant="caption" color="text.secondary">
                {t.currency}
              </Typography>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", fontSize: 11, whiteSpace: "nowrap" }}
            >
              fee {t.our_fee?.toLocaleString("ru-RU") || "0"} · rate{" "}
              {t.our_rate || "—"}
            </Typography>
          </Box>
        );

      case "paid":
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              color: t.paid_amount
                ? theme.palette.success.main
                : "text.secondary",
            }}
          >
            {t.paid_amount?.toLocaleString("ru-RU") || "0"}
          </Typography>
        );

      case "date": {
        const date = formatDate(t.created_at);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box>
              <Typography variant="body2" sx={{ fontSize: 12.5 }} noWrap>
                {date.date}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11 }}
              >
                {date.time}
              </Typography>
            </Box>
            {t.updated_at && (
              <Tooltip
                title={`Обновлено: ${formatDate(t.updated_at).date} ${
                  formatDate(t.updated_at).time
                }`}
              >
                <EventRepeat
                  sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </Box>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Paper
      sx={{
        width: "100%",
        // Заполняет обёртку и не растёт под содержимое: иначе Paper вытягивался
        // на всю таблицу, обрезался родителем, и прокручивать было нечего —
        // maxHeight у TableContainer считался от этой раздутой высоты.
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        boxShadow: "none",
        borderRadius: "15px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Номер заказа
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={idType}
            onChange={(_, value) => value && setIdType(value)}
            aria-label="Источник номера заказа"
          >
            <ToggleButton value="merchant" sx={{ py: 0.4 }}>
              Мерчанта
            </ToggleButton>
            <ToggleButton value="provider" sx={{ py: 0.4 }}>
              Провайдера
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={dense ? "dense" : "comfortable"}
            onChange={handleDensity}
            aria-label="Плотность таблицы"
          >
            <ToggleButton value="dense" sx={{ py: 0.4 }}>
              Компактно
            </ToggleButton>
            <ToggleButton value="comfortable" sx={{ py: 0.4 }}>
              Свободно
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Настроить колонки">
            <IconButton
              aria-label="Настроить колонки"
              onClick={(event) => setColumnsAnchor(event.currentTarget)}
              sx={{ border: `1px solid ${theme.palette.divider}` }}
            >
              <ViewColumnRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Menu
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, py: 0.5, display: "block", fontWeight: 700 }}
        >
          Колонки
        </Typography>
        <Divider sx={{ my: 0.5 }} />
        {COLUMNS.map((column) => (
          <MenuItem
            key={column.id}
            dense
            onClick={() => toggleColumn(column.id)}
          >
            <Checkbox
              size="small"
              checked={Boolean(visible[column.id])}
              sx={{ p: 0.5, mr: 1 }}
            />
            <ListItemText primary={column.label} />
          </MenuItem>
        ))}
      </Menu>

      <TableContainer
        sx={{
          borderRadius: 0,
          border: 0,
          // Заполняет обёртку, а не долю экрана: прокрутка нужна здесь,
          // потому что залипающая шапка требует, чтобы скроллером был
          // именно TableContainer.
          maxHeight: "100%",
        }}
      >
        <Table stickyHeader size={dense ? "small" : "medium"}>
          <TableHead>
            <TableRow>
              {shownColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={
                    column.id === "amount" || column.id === "paid"
                      ? "right"
                      : "left"
                  }
                  sx={{ whiteSpace: "nowrap", padding: cellPadding }}
                >
                  {column.label}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ padding: cellPadding }}>
                Действия
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {transactions.map((t) => (
              <TableRow
                key={t.id}
                hover
                tabIndex={0}
                onClick={() => {
                  setTransaction(t);
                  onOpenDetails?.(t);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    setTransaction(t);
                    onOpenDetails?.(t);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  "&:focus-visible": {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: -2,
                  },
                }}
              >
                {shownColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={
                      column.id === "amount" || column.id === "paid"
                        ? "right"
                        : "left"
                    }
                    sx={{ padding: cellPadding }}
                  >
                    {renderCell(column.id, t)}
                  </TableCell>
                ))}

                <TableCell align="center" sx={{ padding: cellPadding }}>
                  <Box display="flex" justifyContent="center" gap={0.5}>
                    <Tooltip title="Редактировать">
                      <IconButton
                        aria-label="Редактировать транзакцию"
                        size="small"
                        sx={{
                          width: 32,
                          height: 32,
                          color: theme.palette.info.main,
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.info.main,
                              0.12,
                            ),
                          },
                        }}
                        onClick={(event) => {
                          stop(event);
                          setTransaction(t);
                          setChangeType("amount");
                          dialogRef?.current?.openDialog();
                        }}
                      >
                        <Edit fontSize="inherit" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Отправить колбэк">
                      <IconButton
                        aria-label="Отправить колбэк"
                        size="small"
                        onClick={(event) => {
                          stop(event);
                          sendCallBack(t.id);
                        }}
                        sx={{
                          width: 32,
                          height: 32,
                          color: theme.palette.warning.main,
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.warning.main,
                              0.12,
                            ),
                          },
                        }}
                      >
                        <Replay
                          fontSize="inherit"
                          sx={{
                            "&:hover": {
                              animation: `${rotate360} 1.5s linear infinite`,
                            },
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TransactionsTable;
